import "./Shops.scss";
import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import UserPassword from "./../../assets/user-password.svg";
import ShowUser from "./../../assets/show-user.svg";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import Whats from "./../../assets/Whatsapp.svg";
import LinkIcon from "./../../assets/linkicon.png";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { ShopsColumns } from "../../SourceData";
import MessageModals from "../../components/MessageModals/MessageModals";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import { DatePicker, message } from "antd";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import CreateEditShop from "./CreateEditShop";
import usePermissions from "../../hooks/usePermissions";
import SwipeableModals from "../Home/SwipeableModals/SwipeableModals";

const { RangePicker } = DatePicker;

const Shops = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const { hasPermission } = usePermissions();
  // State management
  const [shops, setShops] = useState([]);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [country, setCountry] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [workScope, setWorkScope] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [countries, setCountries] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [cities, setCities] = useState([]);
  const [user, setUser] = useState({});

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);

  const handleEditShop = (shop) => {
    setSelectedShop(shop);
    setIsDrawerOpen(true);
  };

  const handleCreateShop = () => {
    setSelectedShop(null);
    setIsDrawerOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1, // API expects 1-based page numbers
        limit: pageSize,
      });
      params.append("type", "shop");

      if (query) {
        params.append("search", query);
      }
      if (country) {
        params.append("country", country);
      }
      if (province) {
        params.append("province", province);
      }
      if (city) {
        params.append("city", city);
      }
      if (workScope) {
        params.append("work_scope", workScope);
      }
      if (dateRange) {
        if (dateRange[0]) {
          params.append("date_from", dateRange[0].format("YYYY-MM-DD"));
        }
        if (dateRange[1]) {
          params.append("date_to", dateRange[1].format("YYYY-MM-DD"));
        }
      }

      const res = await api.get(`/vendors?${params.toString()}`);

      // Set the shops data
      setShops(res.data.data || []);

      // Use the total count from the API response for proper pagination
      setTotalRows(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching shops:", error);
      // Reset data on error
      setShops([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [query, country, province, city, workScope, dateRange, page, pageSize]);

  // Fetch countries data
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data || []);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountries([]);
      }
    };

    getCountries();
  }, []);

  // Fetch governorates data when country changes
  useEffect(() => {
    const getGovernorates = async () => {
      if (!country) {
        setGovernorates([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          country_id: country,
        });

        const res = await api.get(`/Governorates?${params.toString()}`);
        setGovernorates(res.data.data || []);
        // Reset province and city when country changes
        setProvince("");
        setCity("");
      } catch (error) {
        console.error("Error fetching governorates:", error);
        setGovernorates([]);
      }
    };

    getGovernorates();
  }, [country]);

  // Fetch cities data when governorate changes
  useEffect(() => {
    const getCities = async () => {
      if (!province) {
        setCities([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          governorate_id: province,
        });

        const res = await api.get(`/cities?${params.toString()}`);
        setCities(res.data.data || []);
        // Reset city when province changes
        setCity("");
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };

    getCities();
  }, [province]);

  const handleOpen = (view) => {
    setIsOpen(true);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(0);
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleOpenDelete = (shop) => {
    setSelectedShop(shop);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedShop(null);
  };

  const handleActive = async (id) => {
    setLoading(true);
    try {
      await api.put(`/vendors/${id}/activateToggle`);
      getData();
      message.success("تم تعديل حالة المستخدم بنجاح");
    } catch (error) {
      console.error("Error toggling user status:", error);
      message.error("حدث خطأ أثناء تعديل حالة المستخدم");
    } finally {
      setLoading(false);
    }
  };

  // Reset to first page when filters change
  const handleFilterChange = (filterSetter) => (value) => {
    filterSetter(value);
    setPage(0);
  };

  const actionColumn = [
    hasPermission(30) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active === 1;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 300,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <Link className="cell-btn" to={params?.row?.url} target="_blank">
              <img src={LinkIcon} alt="" style={{ padding: "5px" }} />
            </Link>
            <Link
              className="cell-btn"
              to={`https://wa.me/2${params?.row?.whatsapp}`}
              target="_blank"
            >
              <img src={Whats} alt="" />
            </Link>
            {hasPermission(35) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setUser(params.row);
                  handleOpen("changeVendorPassword");
                }}
              >
                <img src={UserPassword} alt="" />
              </div>
            )}
            {hasPermission(34) && (
              <Link className="cell-btn" to={`/partners/${params.row.id}`}>
                <img src={ShowUser} alt="" />
              </Link>
            )}
            {hasPermission(32) && (
              <div
                className="cell-btn"
                onClick={() => handleEditShop(params.row)}
              >
                <img src={EditUser} alt="" />
              </div>
            )}
            {hasPermission(33) && (
              <div
                className="cell-btn"
                onClick={() => handleOpenDelete(params.row)}
              >
                {params.row.id !== 1 && <img src={DeleteUser} alt="" />}
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean); // Remove false values from hasPermission checks

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">المتاجر</p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder={"بحث..."}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <RangePicker
              format={"MMM D"}
              style={{
                width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                padding: "0px 10px",
                direction: "ltr",
              }}
              placeholder={["من", "الي"]}
              className="range-picker"
              onChange={handleDateRangeChange}
              value={dateRange}
            />
          </div>
          {hasPermission(31) && (
            <BlueButton
              text="اضافة متجر جديد"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              onClick={handleCreateShop}
              icon={<img src={AddGif} alt="" />}
            />
          )}
        </div>

        <div className="filters-container">
          <FormControl
            sx={{
              width: isMobile ? "100%" : "140px",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              البلد
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={country}
              onChange={(e) => handleFilterChange(setCountry)(e.target.value)}
            >
              <MenuItem value="">الجميع</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "140px",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              المحافظة
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={province}
              onChange={(e) => handleFilterChange(setProvince)(e.target.value)}
              disabled={!country}
            >
              <MenuItem value="">الجميع</MenuItem>
              {governorates.map((gov) => (
                <MenuItem key={gov.id} value={gov.id}>
                  {gov.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "140px",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              المدينة
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={city}
              onChange={(e) => handleFilterChange(setCity)(e.target.value)}
              disabled={!province}
            >
              <MenuItem value="">الجميع</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "140px",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              نطاق العمل
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={workScope}
              onChange={(e) => handleFilterChange(setWorkScope)(e.target.value)}
            >
              <MenuItem value="">الجميع</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ShopsColumns.concat(actionColumn)}
          rows={shops.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          rowHeight={80}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={`/vendors/${selectedShop?.id}`}
        onClose={handleCloseDelete}
        onDataChange={getData}
      />

      <CreateEditShop
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        initialData={selectedShop}
        onSuccess={getData}
        type="shop"
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView="changeVendorPassword"
        user={user}
        onDataChange={getData}
      />
    </div>
  );
};

export default Shops;
